// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { PdfSettingsApiParameter, setPdfSettings as generatePdfApi } from "@bg/App/Services/WishListService";
import { createHandlerChainRunner, Handler } from "@insite/client-framework/HandlerCreator";

interface Props {
    apiParameter: PdfSettingsApiParameter;
    apiResult: { successful: boolean; pdfFileUrl?: string; error?: any };
}

type Parameter = { id: string; sendPdfByEmail: boolean; productsImages: any } & {
    onComplete?: (result: any) => void;
    onError?: (e: any) => void;
};

type HandlerType = Handler<Parameter, Props>;

export const DispatchBeginAction: HandlerType = props => {
    const { id, sendPdfByEmail } = props.parameter;
    const {
        withSpecSheet,
        productsInlineFormat,
        projectName,
        ThirdPartyQuoteClientCity,
        ThirdPartyQuoteClientEmail,
        ThirdPartyQuoteClientPhoneNumber,
        ThirdPartyQuoteClientName,
        ThirdPartyQuoteClientPostalCode,
        ThirdPartyQuoteClientProvince,
        ThirdPartyQuoteClientStreetAddress1,
        ThirdPartyQuoteClientStreetAddress2,
        ThirdPartyQuoteWebsite,
        ThirdPartyQuoteExpirationDate,
        ThirdPartyQuoteFreightCharges,
        ThirdPartyQuoteLabourCharges,
        ThirdPartyQuotePriceDisplayOption,
        ThirdPartyQuoteTaxDisplayOption,
        pdfType,
        generalNotes,
    } = (props.getState() as any).pdfSettings.parameters;
    const expirationDate = ThirdPartyQuoteExpirationDate
        ? new Date(ThirdPartyQuoteExpirationDate).toLocaleDateString()
        : null;

    props.apiParameter = {
        id,
        withSpecSheet,
        productsInlineFormat,
        projectName,
        ThirdPartyQuoteClientCity,
        ThirdPartyQuoteClientEmail,
        ThirdPartyQuoteClientPhoneNumber,
        ThirdPartyQuoteClientName,
        ThirdPartyQuoteClientPostalCode,
        ThirdPartyQuoteClientProvince,
        ThirdPartyQuoteClientStreetAddress1,
        ThirdPartyQuoteClientStreetAddress2,
        ThirdPartyQuoteWebsite,
        ThirdPartyQuoteExpirationDate: expirationDate,
        ThirdPartyQuoteFreightCharges,
        ThirdPartyQuoteLabourCharges,
        ThirdPartyQuotePriceDisplayOption,
        ThirdPartyQuoteTaxDisplayOption,
        pdfType,
        sendPdfByEmail,
        productsImages: props.parameter.productsImages,
        generalNotes,
    };
};

export const CallApiWithParameters: HandlerType = async props => {
    props.apiResult = await generatePdfApi(props.apiParameter);
};

export const CallOnError: HandlerType = props => {
    if (!props.apiResult.successful && props.parameter.onError) {
        props.parameter.onError(props.apiResult.error);
    }
};

export const CallOnComplete: HandlerType = props => {
    if (props.apiResult.successful && props.parameter.onComplete) {
        props.parameter.onComplete(props.apiResult);
    }
};

export const chain = [DispatchBeginAction, CallApiWithParameters, CallOnError, CallOnComplete];

const generatePdf = createHandlerChainRunner(chain, "GeneratePdf");
export default generatePdf;
